import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { Fire, Warning, Clock, ShieldCheck, MagnifyingGlass, ArrowsClockwise, Calendar, CheckCircle, Repeat, Wrench, Camera, Sparkle } from "@phosphor-icons/react";
import ApplianceNameplateScan from "@/components/ApplianceNameplateScan";

/**
 * AdminApplianceIQ — proactive-replacement dashboard for the "big 4"
 * appliances + HVAC + water heaters + washer/dryer.
 * Alerts fire on: age vs. expected lifespan, warranty expiry, repeat
 * repair frequency. Data lives on the existing `assets` collection.
 */
export default function AdminApplianceIQ() {
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/appliance-iq/summary"),
        api.get("/appliance-iq/list", { params: { filter, category, address: q } }),
      ]);
      setSummary(s.data);
      setItems(l.data?.appliances || []);
    } finally { setLoading(false); }
  }, [filter, category, q]);

  useEffect(() => { load(); }, [load]);

  const saveInstall = async (asset, iso) => {
    setSaving(asset._id);
    try {
      await api.patch(`/assets/${asset._id}`, { install_date: iso });
      toast.success("Install date saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not save"));
    } finally { setSaving(null); }
  };

  const saveWarranty = async (asset, iso) => {
    setSaving(asset._id);
    try {
      await api.patch(`/assets/${asset._id}`, { warranty_end: iso });
      toast.success("Warranty end saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not save"));
    } finally { setSaving(null); }
  };

  const categoryOptions = [
    { slug: "", label: "All types" },
    { slug: "water_heater", label: "Water Heater" },
    { slug: "fridge", label: "Fridge" },
    { slug: "refrigerator", label: "Refrigerator" },
    { slug: "range", label: "Range" },
    { slug: "stove", label: "Stove" },
    { slug: "oven", label: "Oven" },
    { slug: "dishwasher", label: "Dishwasher" },
    { slug: "microwave", label: "Microwave" },
    { slug: "washer", label: "Washer" },
    { slug: "dryer", label: "Dryer" },
    { slug: "hvac", label: "HVAC" },
    { slug: "furnace", label: "Furnace" },
    { slug: "ac", label: "A/C" },
  ];

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="h-12 w-12 bg-gradient-to-br from-amber-500 to-red-500 rounded-lg flex items-center justify-center border-2 border-slate-900 shadow-[3px_3px_0px_#0f172a]">
            <Wrench weight="fill" size={26} className="text-white"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-3xl uppercase tracking-tight leading-none">ApplianceIQ</div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-1">
              Proactive replacement · never get caught by a Saturday-night failure
            </div>
          </div>
          <button onClick={() => setScanOpen(true)} data-testid="iq-scan-nameplate-btn"
                  className="h-10 px-3 border-2 border-slate-900 bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-700 hover:to-fuchsia-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
            <Sparkle weight="fill" size={13}/> Scan Nameplate
          </button>
          <button onClick={load} data-testid="iq-refresh"
                  className="h-10 w-10 border-2 border-slate-900 bg-white flex items-center justify-center hover:bg-slate-100">
            <ArrowsClockwise size={18} weight="bold"/>
          </button>
        </div>

        {scanOpen && <ApplianceNameplateScan onClose={() => setScanOpen(false)} onSaved={load}/>}

        {/* Alert cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            <AlertCard label="Replace Now" count={summary.replace_now} color="red" Icon={Fire}
                       active={filter === "replace_now"} onClick={() => setFilter(filter === "replace_now" ? "all" : "replace_now")}/>
            <AlertCard label="Repeat Repair" count={summary.repeat_repair} color="amber" Icon={Repeat}
                       active={filter === "repeat_repair"} onClick={() => setFilter(filter === "repeat_repair" ? "all" : "repeat_repair")}/>
            <AlertCard label="Aging" count={summary.aging} color="orange" Icon={Warning}
                       active={filter === "aging"} onClick={() => setFilter(filter === "aging" ? "all" : "aging")}/>
            <AlertCard label="Warranty ⏳" count={summary.warranty_expiring} color="fuchsia" Icon={Clock}
                       active={filter === "warranty_expiring"} onClick={() => setFilter(filter === "warranty_expiring" ? "all" : "warranty_expiring")}/>
            <AlertCard label="OK" count={summary.ok} color="emerald" Icon={ShieldCheck}
                       active={filter === "ok"} onClick={() => setFilter(filter === "ok" ? "all" : "ok")}/>
          </div>
        )}

        {/* Filters */}
        <div className="mb-4 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass weight="bold" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input list="known-addresses" autoComplete="off" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Filter by address…"
                   data-testid="iq-address-search"
                   className="w-full h-10 pl-9 pr-3 border-2 border-slate-300 rounded-md text-sm font-medium focus:border-cyan-500 focus:outline-none"/>
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
                  data-testid="iq-category-filter"
                  className="h-10 px-3 border-2 border-slate-300 rounded-md text-sm font-black uppercase tracking-widest focus:border-cyan-500 focus:outline-none">
            {categoryOptions.map(c => (
              <option key={c.slug} value={c.slug}>{c.label}</option>
            ))}
          </select>
          {filter !== "all" && (
            <button onClick={() => setFilter("all")} data-testid="iq-clear-filter"
                    className="h-10 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 text-xs font-black uppercase tracking-widest rounded-md">
              Clear · {filter.replace(/_/g, " ")}
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white border-2 border-slate-900 rounded-lg overflow-hidden shadow-[3px_3px_0px_#0f172a]">
          <div className="px-4 py-2 border-b-2 border-slate-900 bg-slate-100">
            <div className="text-xs font-black uppercase tracking-widest text-slate-700">
              {loading ? "Loading…" : `${items.length} appliance${items.length === 1 ? "" : "s"}`}
              {filter !== "all" && ` · filtered: ${filter.replace(/_/g, " ")}`}
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-200">
            {!loading && items.length === 0 && (
              <div className="p-10 text-center">
                <Wrench weight="light" size={40} className="mx-auto text-slate-300"/>
                <div className="text-sm font-black text-slate-500 mt-2">
                  {filter !== "all" ? "Nothing in this bucket · good news!" : "No appliances yet"}
                </div>
                {filter === "all" && (
                  <div className="text-xs text-slate-400 mt-1">Appliances auto-appear from your `assets` collection. Add categories: water_heater, fridge, range, HVAC, etc.</div>
                )}
              </div>
            )}
            {items.map(a => (
              <ApplianceRow
                key={a._id}
                a={a}
                saving={saving === a._id}
                onSaveInstall={(iso) => saveInstall(a, iso)}
                onSaveWarranty={(iso) => saveWarranty(a, iso)}
              />
            ))}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

function AlertCard({ label, count, color, Icon, active, onClick }) {
  const bgMap = {
    red: active ? "bg-red-500 border-red-700 text-white shadow-[3px_3px_0px_#7f1d1d]" : "bg-white border-red-300 text-red-700 hover:border-red-500",
    amber: active ? "bg-amber-500 border-amber-700 text-slate-900 shadow-[3px_3px_0px_#78350f]" : "bg-white border-amber-300 text-amber-800 hover:border-amber-500",
    orange: active ? "bg-orange-500 border-orange-700 text-white shadow-[3px_3px_0px_#7c2d12]" : "bg-white border-orange-300 text-orange-800 hover:border-orange-500",
    fuchsia: active ? "bg-fuchsia-500 border-fuchsia-700 text-white shadow-[3px_3px_0px_#701a75]" : "bg-white border-fuchsia-300 text-fuchsia-800 hover:border-fuchsia-500",
    emerald: active ? "bg-emerald-500 border-emerald-700 text-white shadow-[3px_3px_0px_#064e3b]" : "bg-white border-emerald-300 text-emerald-700 hover:border-emerald-500",
  };
  return (
    <button onClick={onClick} data-testid={`iq-alert-${label.toLowerCase().replace(/[^a-z]/g,"-")}`}
            className={`p-3 border-2 rounded-lg transition-all text-left ${bgMap[color]}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon weight="fill" size={16}/>
        <div className="text-[10px] font-black uppercase tracking-widest">{label}</div>
      </div>
      <div className="text-3xl font-display font-black tabular-nums leading-none">{count}</div>
    </button>
  );
}

function ApplianceRow({ a, saving, onSaveInstall, onSaveWarranty }) {
  const [editing, setEditing] = useState(null); // "install" | "warranty" | null
  const alert = a.alert || {};
  const statusColor = {
    replace_now: "bg-red-100 text-red-800 border-red-300",
    repeat_repair: "bg-amber-100 text-amber-800 border-amber-300",
    aging: "bg-orange-100 text-orange-800 border-orange-300",
    warranty_expiring: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
    warranty_expired: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
    ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  }[alert.status] || "bg-slate-100 text-slate-600 border-slate-200";
  const statusLabel = {
    replace_now: "🔥 REPLACE NOW",
    repeat_repair: "🔁 REPEAT REPAIR",
    aging: "⚠️ AGING",
    warranty_expiring: "⏳ WARRANTY",
    warranty_expired: "⏰ WARRANTY EXPIRED",
    ok: "✓ OK",
  }[alert.status] || alert.status;

  const kind = String(a.subcategory || a.category || "").replace(/_/g, " ");

  return (
    <div className="p-3 hover:bg-slate-50 flex flex-wrap items-start gap-3" data-testid={`iq-row-${a._id}`}>
      {/* Name + address */}
      <div className="flex-1 min-w-[220px]">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="font-black text-sm text-slate-900 truncate">{a.name || kind}</div>
            <div className="text-xs text-slate-600 truncate">{a.address || "—"} {a.location ? `· ${a.location}` : ""}</div>
            <div className="mt-1 flex flex-wrap gap-1">
              <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded">{kind}</span>
              {a.make && <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-100 text-cyan-800 px-1.5 py-0.5 rounded">{a.make}</span>}
              {a.serial && <span className="text-[9px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">SN:{String(a.serial).slice(-6)}</span>}
            </div>
          </div>
          <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded border-2 whitespace-nowrap ${statusColor}`}>
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Install date + Warranty end quick-inputs */}
      <div className="flex flex-col gap-1.5 min-w-[280px]">
        <QuickDate label="Installed" value={a.install_date} saving={saving}
                   editing={editing === "install"} onEdit={() => setEditing("install")}
                   onCancel={() => setEditing(null)}
                   onSave={(iso) => { onSaveInstall(iso); setEditing(null); }}
                   testidPrefix={`iq-install-${a._id}`}/>
        <QuickDate label="Warranty" value={a.warranty_end} saving={saving}
                   editing={editing === "warranty"} onEdit={() => setEditing("warranty")}
                   onCancel={() => setEditing(null)}
                   onSave={(iso) => { onSaveWarranty(iso); setEditing(null); }}
                   testidPrefix={`iq-warranty-${a._id}`}/>
      </div>

      {/* Age % + reasons */}
      <div className="min-w-[180px] max-w-[240px]">
        {alert.age_pct !== null && alert.age_pct !== undefined ? (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Age</span>
              <span className="text-xs font-black text-slate-800 tabular-nums">{alert.age_pct}% · {alert.age_years}y / {alert.lifespan_years}y</span>
            </div>
            <div className="h-2 bg-slate-200 rounded overflow-hidden">
              <div className={`h-full transition-all ${
                alert.age_pct >= 100 ? "bg-red-500" :
                alert.age_pct >= 90 ? "bg-red-400" :
                alert.age_pct >= 75 ? "bg-orange-400" :
                alert.age_pct >= 50 ? "bg-amber-400" : "bg-emerald-400"
              }`} style={{ width: `${Math.min(100, alert.age_pct)}%` }}/>
            </div>
          </div>
        ) : (
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Set install date to see age
          </div>
        )}
        {alert.reasons?.length > 0 && (
          <div className="mt-1 text-[10px] text-slate-600 leading-tight">
            {alert.reasons.slice(0, 2).join(" · ")}
          </div>
        )}
        {alert.service_count_365 > 0 && (
          <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-amber-700 flex items-center gap-1">
            <Repeat weight="fill" size={10}/> {alert.service_count_365} repair{alert.service_count_365 === 1 ? "" : "s"} · past year
          </div>
        )}
      </div>
    </div>
  );
}

function QuickDate({ label, value, editing, onEdit, onCancel, onSave, saving, testidPrefix }) {
  const [draft, setDraft] = useState(value ? String(value).slice(0, 10) : "");
  useEffect(() => { setDraft(value ? String(value).slice(0, 10) : ""); }, [value]);
  const displayValue = value ? (value.length >= 10 ? value.slice(0, 10) : value) : null;

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 w-16">{label}</span>
        <input type="date" value={draft} onChange={(e) => setDraft(e.target.value)}
               data-testid={`${testidPrefix}-input`} autoFocus
               className="flex-1 h-8 px-2 border-2 border-cyan-500 rounded text-xs font-medium"/>
        <button onClick={() => onSave(draft)} data-testid={`${testidPrefix}-save`}
                disabled={saving || !draft}
                className="h-8 px-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-[10px] font-black uppercase rounded">
          {saving ? "…" : "Save"}
        </button>
        <button onClick={onCancel} data-testid={`${testidPrefix}-cancel`}
                className="h-8 px-2 border border-slate-300 bg-white hover:bg-slate-100 text-slate-600 text-[10px] font-black uppercase rounded">
          Cancel
        </button>
      </div>
    );
  }
  return (
    <button onClick={onEdit} data-testid={`${testidPrefix}-edit`}
            className="flex items-center gap-1 group hover:bg-slate-100 rounded p-0.5 -mx-0.5 text-left">
      <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 w-16">{label}</span>
      <span className={`text-xs ${displayValue ? "font-bold text-slate-800" : "text-slate-400 italic"}`}>
        {displayValue || "add date"}
      </span>
      <Calendar size={12} className="text-slate-400 group-hover:text-cyan-600 transition-colors"/>
    </button>
  );
}
